/**
 * Texts (`en`) of the stock count of the BackOffice (OBR-147). Same keys as `count.ca.ts` (i18n.test.ts). Vocabulary: «stock count»
 * (never «inventory»).
 */

export const countEn = {
  'backoffice.stockCounts': {
    // The list
    title: 'Stock counts',
    subtitle:
      'Count the real stock, compare it with the theoretical one and adjust the differences.',
    create: 'New count',
    searchLabel: 'Search',
    filterStatus: 'Status',
    filterScope: 'Scope',
    filterFrom: 'From',
    filterTo: 'To',
    allStatuses: 'All statuses',
    allScopes: 'All scopes',
    status_open: 'Open',
    status_validated: 'Validated',
    status_cancelled: 'Cancelled',
    scope_tenant: 'Whole workshop',
    scope_zone: 'Zone',
    scope_section: 'Section',
    scope_location: 'Location',
    scope_article: 'Article',
    scopeOf_tenant: 'Whole workshop',
    scopeOf_zone: 'Zone {{name}}',
    scopeOf_section: 'Section {{name}}',
    scopeOf_location: 'Location {{name}}',
    scopeOf_article: 'Article {{name}}',
    scopeUnknown: '—',
    colNumber: 'Number',
    colScope: 'Scope',
    colStatus: 'Status',
    colProgress: 'Lines',
    colDate: 'Date',
    colBy: 'Created by',
    progressOpen: '{{counted}} of {{total}} counted',
    progressValidated: '{{adjusted}} adjusted of {{total}}',
    progressCancelled: '{{total}} lines not adjusted',
    empty: 'There are no counts yet. Create one to count the real stock.',
    emptyFiltered: 'No count matches the filters.',
    loadError: 'The counts could not be loaded. Try again.',
    listLimit: 'Showing the {{count}} most recent counts.',
    unknownPerson: '—',

    // The creation dialog
    createTitle: 'New stock count',
    createBody:
      'A snapshot of the theoretical stock of the chosen scope is saved, lot by lot and place by place. Stock is not blocked while you count: movements carry on.',
    createScopeLabel: 'What do you want to count?',
    createZoneLabel: 'Zone',
    createSectionLabel: 'Section',
    createLocationLabel: 'Location',
    createArticleLabel: 'Article',
    createTargetPlaceholder: 'Choose one',
    createNoTargets: 'There is nothing of this kind in the workshop structure yet.',
    createNoteLabel: 'Note (optional)',
    createNotePlaceholder: 'For example: month-end count',
    createConfirm: 'Create the count',

    // The count screen
    back: 'All counts',
    notFound: 'This count does not exist or you cannot see it.',
    detailLoadError: 'The count could not be loaded. Try again.',
    linesLoadError: 'The lines of the count could not be loaded. Try again.',
    createdBy: 'Created by {{name}} on {{date}}',
    validatedBy: 'Validated by {{name}} on {{date}}',
    cancelledBy: 'Cancelled by {{name}} on {{date}}',
    cancelReasonLine: 'Reason: {{reason}}',
    noteLine: 'Note: {{note}}',
    validate: 'Validate the count',
    cancel: 'Cancel the count',
    addFound: 'Add a lot found',
    filterAll: 'All',
    filter_pending: 'Pending',
    filter_match: 'Matches',
    filter_short: 'Short',
    filter_over: 'Over',
    filterAria: 'Filter the lines by the result of the count',
    state_pending: 'Not counted',
    state_match: 'Matches',
    state_short: 'Short',
    state_over: 'Over',
    stateShortQuantity: 'Short by {{quantity}} {{unit}}',
    stateOverQuantity: 'Over by {{quantity}} {{unit}}',
    colLot: 'Lot',
    colPlace: 'Place',
    colTheoretical: 'Theoretical',
    colNow: 'Now',
    colAtValidation: 'At validation',
    colCounted: 'Counted',
    colDifference: 'Difference',
    colAdjustment: 'Adjustment',
    noLot: 'No lot',
    noPlace: 'No place',
    expiresOn: 'Expires on {{date}}',
    noExpiry: 'No expiry',
    newLotTag: 'New lot',
    foundTag: 'Found',
    changedTag: 'Changed',
    changedHint:
      'There are now {{quantity}} {{unit}}: someone has moved stock of this lot since the count was created.',
    currentLoading: 'Reading the stock as it is now…',
    currentError:
      'The stock as it is now could not be read. The count can go on, but you will not be warned if it has changed.',
    confirmTheoretical: 'Confirm theoretical',
    confirmGroup: 'Confirm the theoretical of the group',
    confirmNegative: 'A negative balance cannot be confirmed: count it for real.',
    countedAria: 'Counted quantity of {{article}}, lot {{lot}}',
    countedInvalid: 'Enter a valid quantity (0 or more).',
    saving: 'Saving…',
    saved: 'All saved',
    saveError: 'What you wrote could not be saved. Try again.',
    saveRetry: 'Try again',
    groupLines: '{{count}} lines',
    warnChanged:
      'The theoretical quantity has changed in {{count}} lines since the count was created. When you validate, the adjustment is worked out on the stock as it is now, not on the snapshot.',
    warnPending:
      'There are {{count}} lines not counted: they will not be touched when you validate.',
    validatedBanner:
      'Count validated: {{adjusted}} adjustments, {{matched}} lines match and {{lots}} new lots.',
    readOnlyValidated: 'This count is validated and can no longer be edited.',
    readOnlyCancelled: 'This count is cancelled and adjusted nothing.',
    emptyLines: 'This scope had no stock in the snapshot. You can add the lots you find.',
    noLinesFiltered: 'No line matches this filter.',
    adjustmentNone: 'No adjustment',
    adjustmentNotCounted: 'Not counted',
    viewMovements: 'Show the movements',

    // Validation
    validateTitle: 'Validate the count',
    validateBody:
      'An adjustment movement with the reason «Recount» will be created for each line with a difference. Once validated, the count can no longer be edited.',
    validateSummaryAdjust:
      '{{count}} adjustments: {{increases}} over, {{decreases}} short and {{lots}} new lots.',
    validateNothing: 'There is no difference: no movement will be created.',
    validateMatched: '{{count}} lines match and create no movement.',
    validateNotCounted: '{{count}} lines not counted: they will not be touched.',
    validateChanged:
      '{{count}} lines with a changed theoretical quantity: the adjustment is worked out on the stock as it is now.',
    validateBlocked: 'These lines cannot be adjusted as they are:',
    validateBlockedHint: 'Fix them before validating.',
    validateAdjustmentsTitle: 'Adjustments that will be created',
    validateAdjustLine: '{{article}} · {{lot}} · {{place}}: {{from}} → {{to}} ({{delta}} {{unit}})',
    validateNewLotLine: '{{article}} · new lot · {{place}}: {{to}} {{unit}}',
    validateMore: 'and {{count}} more',
    validateConfirm: 'Validate and adjust',
    validateCurrentLoading: 'Reading the stock as it is now…',
    validateCurrentError:
      'The stock as it is now could not be read. You can validate anyway: the server checks it.',
    validateFailedLine: 'Line {{article}} · {{lot}} · {{place}}: {{message}}',

    // Cancellation
    cancelTitle: 'Cancel the count',
    cancelBody: 'The count is cancelled and nothing is adjusted. A reason is required.',
    cancelLabel: 'Reason for cancelling',
    cancelPlaceholder: 'For example: we picked the wrong scope',
    cancelConfirm: 'Cancel the count',

    // Add a lot found
    foundTitle: 'Add a lot found',
    foundBody:
      'A lot you found that was not in the snapshot. If it already exists in the system, choose it; if not, it will be created as a new adjustment lot when you validate.',
    foundArticle: 'Article',
    foundArticlePlaceholder: 'Search an article',
    foundMode: 'Type of lot',
    foundModeExisting: 'A lot that already exists',
    foundModeNew: 'A new lot',
    foundLot: 'Lot',
    foundLotPlaceholder: 'Choose a lot',
    foundLotsLoading: 'Reading the lots…',
    foundLotsError: 'The lots of this article could not be read.',
    foundNoLots: 'This article has no lots yet: choose «A new lot».',
    foundCode: 'Supplier lot code (optional)',
    foundExpiry: 'Expiry',
    foundExpiryRequired: 'Required for this article.',
    foundExpiryOptional: 'Optional.',
    foundExpiryInvalid: 'Enter a valid date.',
    foundPlace: 'Place',
    foundPlaceHint: 'It must be inside the scope of the count.',
    foundPlaceRequired: 'Choose a place.',
    foundQuantity: 'Counted quantity ({{unit}})',
    foundQuantityBare: 'Counted quantity',
    foundQuantityInvalid: 'Enter a valid quantity.',
    foundNote: 'Note (optional)',
    foundConfirm: 'Add',
    foundProduction:
      'This article is only produced: its lot is created by a work order. Choose a lot that already exists.',
    foundNoTracked: 'This article has no stock control.',

    // 2nd pass: confirming with the balance of now, expiry of an existing lot, created lot, validating with everything saved
    confirmCurrent: 'Confirm current',
    confirmCurrentHint:
      'It has changed since the snapshot: the {{quantity}} {{unit}} of now are confirmed, not the snapshot ones.',
    confirmGroupCurrentHint:
      '{{count}} lines have changed since the snapshot: their balance of now is confirmed, not the snapshot one.',
    lotExpiryLabel: 'Lot expiry',
    lotExpiryHint:
      'This lot has none and the count adds stock to it: enter it so the count can be validated.',
    lotExpiryAria: 'Expiry of lot {{lot}} of {{article}}',
    lotCreatedTag: 'Lot created',
    validatePreparing: 'Saving the quantities and reading the current state of the count…',
    validateUnsaved:
      'Some quantities could not be saved: without that, the summary would not be what gets validated.',
    validateRetry: 'Try again',
    leaveTitle: 'There are unsaved quantities',
    leaveBody: 'The quantities you wrote could not be saved. If you leave now, they will be lost.',
    leaveConfirm: 'Leave anyway',

    // A found lot that is already in the count
    foundExistsTitle: 'This lot is already in the count at this place',
    foundExistsCounted: 'You already counted {{quantity}} {{unit}}.',
    foundExistsPending: 'You have not counted it yet.',
    foundExistsChoice: 'What do we do with what was already counted?',
    foundExistsAddShort: 'Add it',
    foundExistsReplaceShort: 'Replace it',
    foundExistsAdd: 'Add it: {{from}} + {{quantity}} = {{to}} {{unit}}',
    foundExistsReplace: 'Replace it: it becomes {{to}} {{unit}}',
    foundExpiryExistingRequired:
      'This lot has no expiry and the article requires one: enter it so it can be adjusted.',
    foundLotsTruncated:
      'Only the {{limit}} newest lots are shown. Type the lot number to search for it.',
    foundLotSearch: 'Search a lot by number',
    foundLotSearchPlaceholder: 'For example: MP-L-00042',

    // Creating a count: the structure could not be read
    createStructureError:
      'The structure of the obrador (zones, sections and locations) could not be read: without it a structure scope cannot be chosen.',
    createStructureRetry: 'Try again',

    // RPC errors
    errForbidden: 'You are not allowed to do stock counts.',
    errBadScope: 'The chosen scope is not valid.',
    errTooLarge:
      'The scope has too many lines. Choose a zone, a section, a location or an article.',
    errClientUuidConflict:
      'This operation had already been done with other data. Reopen the dialog and try again.',
    errNotFound: 'This count does not exist or you cannot see it.',
    errNotOpen: 'This count is no longer open: someone may have validated or cancelled it.',
    errBadLine: 'One of the lines is not valid.',
    errBadQuantity: 'The quantity is not valid: it must be 0 or more.',
    errUnknownLine: 'One of the lines no longer belongs to this count. Reload the screen.',
    errUnknownArticle: 'This article does not exist, is deactivated or has no stock control.',
    errUnknownLot: 'This lot does not belong to this article.',
    errBadPlace: 'This place does not belong to the workshop.',
    errOutOfScope: 'This place is outside the scope of the count.',
    errLotRequired: 'A lot is required (an existing one or a new one).',
    errReasonRequired: 'A reason is required.',
    errImmutable: 'A validated or cancelled count cannot be modified.',
    errGeneric: 'It could not be done. Try again.',
  },
} as const;
